'use client'

import { motion, useInView } from 'framer-motion'
import { useRef } from 'react'
import { RouteLink } from '@/lib/router'
import { RevealText } from './reveal-text'
import { MagneticLink } from './magnetic-button'

export function ContactSection() {
  const sectionRef = useRef<HTMLElement>(null)
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' })

  return (
    <section ref={sectionRef} id="contact" className="py-32 md:py-48 px-6 lg:px-12 bg-card">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-12 gap-8 lg:gap-16">
          <div className="md:col-span-7">
            <RevealText className="mb-4">
              <span className="text-xs tracking-[0.3em] uppercase text-accent">
                Start a conversation
              </span>
            </RevealText>
            <RevealText delay={0.1}>
              <h2 className="text-4xl md:text-6xl lg:text-7xl font-serif tracking-tight mb-8">
                Ready to build
                <br />
                <span className="italic text-muted-foreground">something that ships?</span>
              </h2>
            </RevealText>
            <RevealText delay={0.2}>
              <p className="text-xl text-muted-foreground font-serif italic max-w-2xl mb-10">
                Tell us about your website, app, or internal tool - we typically reply within one
                business day.
              </p>
            </RevealText>
            <MagneticLink href="/contact" variant="primary">
              Open Contact Form
            </MagneticLink>
          </div>

          <motion.div
            className="md:col-span-5 space-y-10"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div>
              <span className="text-xs tracking-[0.2em] uppercase text-muted-foreground block mb-2">
                Email
              </span>
              <a
                href="mailto:contact@elementarycreative.com"
                className="inline-block py-2 text-xl font-serif hover:text-accent transition-colors"
              >
                contact@elementarycreative.com
              </a>
            </div>
            <div>
              <span className="text-xs tracking-[0.2em] uppercase text-muted-foreground block mb-2">
                Phone
              </span>
              <a
                href="tel:+16393164005"
                className="inline-block py-2 text-xl font-serif hover:text-accent transition-colors"
              >
                (639) 316-4005
              </a>
            </div>
            <div>
              <span className="text-xs tracking-[0.2em] uppercase text-muted-foreground block mb-2">
                Book a call
              </span>
              <a
                href="https://calendly.com/elementarycreative/30min"
                target="_blank"
                rel="noreferrer"
                className="inline-block py-2 text-xl font-serif hover:text-accent transition-colors"
              >
                Free 30-minute consultation
              </a>
            </div>
            <div>
              <span className="text-xs tracking-[0.2em] uppercase text-muted-foreground block mb-2">
                Availability
              </span>
              <p className="text-xl font-serif">Remote and in-person across Canada</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
